import React, { useCallback, useState } from 'react';
import { X } from 'react-feather';
import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';

export interface FormDisclaimerModalProps {
  open: boolean;
  onClose: () => void;
  onSyncToIConnect: () => void;
  onSubmit: () => void;
  disclaimerEnabled?: boolean;
  showSyncToIConnect?: boolean;
  disclaimerTitle?: string;
  disclaimerText?: string;
  syncToIconnectButtonText?: string;
  submitButtonText?: string;
}

const DEFAULT_TITLE = 'Disclaimer';
const DEFAULT_ICONNECT_TEXT =
  'In case of syncing to iConnect, please note that the draft has been pushed to iConnect. Please use iConnect to submit the final record.';
const DEFAULT_AIDA_ONLY_TEXT = 'Submitting this form will keep save it to the database.';
const DEFAULT_SYNC_BUTTON_TEXT = 'Sync to iConnect';
const DEFAULT_SUBMIT_WITH_SYNC_TEXT = 'Submit Only';
const DEFAULT_SUBMIT_NO_SYNC_TEXT = 'Submit';

function normalizeEscapedNewlines(value: string): string {
  return value.replace(/\\\\r\\\\n|\\\\n|\\\\r/g, '\n').replace(/\\r\\n|\\n|\\r/g, '\n');
}

export const FormDisclaimerModal: React.FC<FormDisclaimerModalProps> = ({
  open,
  onClose,
  onSyncToIConnect,
  onSubmit,
  disclaimerEnabled = true,
  showSyncToIConnect = false,
  disclaimerTitle = DEFAULT_TITLE,
  disclaimerText,
  syncToIconnectButtonText = DEFAULT_SYNC_BUTTON_TEXT,
  submitButtonText,
}) => {
  const [showSynced, setShowSynced] = useState(false);

  const defaultDisclaimerText = showSyncToIConnect ? DEFAULT_ICONNECT_TEXT : DEFAULT_AIDA_ONLY_TEXT;
  const resolvedDisclaimerText = normalizeEscapedNewlines(disclaimerText ?? defaultDisclaimerText);
  const resolvedSubmitButtonText =
    submitButtonText ?? (showSyncToIConnect ? DEFAULT_SUBMIT_WITH_SYNC_TEXT : DEFAULT_SUBMIT_NO_SYNC_TEXT);
  const resolvedSyncButtonText = normalizeEscapedNewlines(syncToIconnectButtonText);
  const resolvedSubmitButtonMultilineText = normalizeEscapedNewlines(resolvedSubmitButtonText);

  const handleSubmit = useCallback(() => {
    onSubmit();
  }, [onSubmit]);

  const onModalClose = useCallback(() => {
    setShowSynced(false);
    onClose();
  }, [onClose]);

  if (!disclaimerEnabled) {
    return null;
  }

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onModalClose} open={open}>
      <Header>
        <Title>{disclaimerTitle}</Title>
        <StyledX onClick={onModalClose} />
      </Header>
      <Content>
        <b>Please Note:</b> {resolvedDisclaimerText}
        {showSyncToIConnect && showSynced && (
          <SyncedMessage>Synced to iConnect and form submitted.</SyncedMessage>
        )}
      </Content>
      <ButtonsContainer>
        {showSyncToIConnect && (
          <Button
            onClick={async () => {
              setShowSynced(false);
              try {
                await onSyncToIConnect();
                setShowSynced(true);
              } catch {
                setShowSynced(false);
              }
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: 'rgb(255, 161, 146)',
              color: '#000',
              '&:hover': {
                backgroundColor: 'rgb(255, 140, 126)',
              },
              whiteSpace: 'pre-line',
            }}
          >
            {resolvedSyncButtonText}
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant="outlined"
          sx={{
            textTransform: 'none',
            color: '#000',
            borderColor: '#000',
            whiteSpace: 'pre-line',
          }}
        >
          {resolvedSubmitButtonMultilineText}
        </Button>
      </ButtonsContainer>
    </Dialog>
  );
};

const StyledX = styled(X)({
  cursor: 'pointer',
  position: 'absolute',
  top: 15,
  right: 10,
  height: 24,
  width: 24,
});

const Header = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '15px 10px',
  borderBottom: '1px solid #d0d0d0',
  position: 'relative',
});

const Title = styled.div({
  fontSize: 20,
  fontWeight: 700,
  color: '#000',
});

const Content = styled.div({
  padding: 30,
  whiteSpace: 'pre-line',
});

const ButtonsContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  gap: 16,
  padding: 30,
});

const SyncedMessage = styled.div({
  padding: '0',
  color: '#2E7D32',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  marginTop: 10,
});
